import { supabase, DEFAULT_TENANT, DEFAULT_COURSE_SLUG } from '@/lib/supabase';
import type { CourseLite, ModuleLite, LessonLite, LessonAttachment, LessonExternalLink, MediaLibraryItem } from '@/types/content';
import { listLessonMaterials } from './lessonMaterialRepo';

// Tipos para o sistema de mídias
export interface MediaMapping {
  id: string;
  tenant_id: string;
  slot: string;
  library_url: string;
  media_type: 'video' | 'image' | 'document' | 'link' | 'audio';
  title: string;
  provider: 'native' | 'youtube' | 'vimeo' | 'bunny' | 'iframe';
  video_id?: string; // ID do vídeo para Vimeo, YouTube, Bunny
  video_library_id?: string; // Library ID para Bunny CDN
  original_url?: string; // Backup da URL original
  config: any;
  order_index: number;
  is_required: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface LessonMediaMapping {
  id: string;
  lesson_id: string;
  media_mapping_id: string;
  slot: string;
  order_index: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface LessonWithMedia extends LessonLite {
  mainVideo?: MediaMapping;
  supportingMedia: MediaMapping[];
  allMedia: MediaMapping[];
}

// Cache para tenant ID - evita SELECTs repetidos
let cachedTenantId: string | null = null;

// Dados mock de fallback
const MOCK_COURSE: CourseLite = {
  id: '1a1f5462-fa9c-4e94-a067-2cdd7168a8d4', // UUID
  slug: DEFAULT_COURSE_SLUG,
  title: 'Formação de Líderes e Mentores',
  summary: 'Curso completo de liderança cristã e mentoria',
  status: 'active'
};

const MOCK_MODULES: ModuleLite[] = [
  {
    id: 'mod-1-uuid-1234-5678-9abc-def0',
    slug: 'modulo-1-formacao-de-lideres-e-mentores',
    title: 'Módulo 1: Fundamentos da Liderança',
    summary: 'Introdução aos princípios básicos de liderança',
    status: 'active',
    order: 1
  },
  {
    id: 'mod-2-uuid-1234-5678-9abc-def1',
    slug: 'modulo-2-comunicacao-efetiva',
    title: 'Módulo 2: Comunicação Efetiva',
    summary: 'Como se comunicar de forma eficaz como líder',
    status: 'active',
    order: 2
  },
  {
    id: 'mod-3-uuid-1234-5678-9abc-def2',
    slug: 'modulo-3-desenvolvimento-equipe',
    title: 'Módulo 3: Desenvolvimento de Equipe',
    summary: 'Técnicas para construir e liderar equipes',
    status: 'active',
    order: 3
  }
];

export async function getTenantId(tenantSlug = DEFAULT_TENANT): Promise<string> {
  // Se já temos o tenant ID em cache, retorna diretamente
  if (cachedTenantId) {
    console.log('✅ Tenant ID retornado do cache:', cachedTenantId);
    return cachedTenantId;
  }

  try {
    console.log('🔄 Buscando tenant ID do banco para slug:', tenantSlug);
    console.log('🔍 Query: from("tenants").select("id").eq("slug", tenantSlug).single()');
    
    const { data, error } = await supabase
      .from('tenants')
      .select('id')
      .eq('slug', tenantSlug)
      .single();
    
    console.log('📝 Resultado da query:', { data, error });
    
    if (error || !data) {
      console.warn('⚠️ Tenant não encontrado, usando UUID padrão');
      // Usar um UUID válido como fallback
      return '00000000-0000-0000-0000-000000000000';
    }
    
    // Armazena no cache e retorna
    cachedTenantId = data.id as string;
    console.log('✅ Tenant ID obtido do banco e armazenado no cache:', cachedTenantId);
    console.log('🔍 Tipo do tenant ID:', typeof cachedTenantId, 'Valor:', cachedTenantId);
    return cachedTenantId;
    
  } catch (error) {
    console.warn('⚠️ Erro ao buscar tenant, usando UUID padrão:', error);
    // Usar um UUID válido como fallback
    return '00000000-0000-0000-0000-000000000000';
  }
}

export async function getCourseBySlug(tenantId: string, courseSlug = DEFAULT_COURSE_SLUG): Promise<CourseLite> {
  try {
    const { data, error } = await supabase
      .from('courses')
      .select('id, slug, title, summary, cover, status, instructor, category')
      .eq('tenant_id', tenantId)
      .eq('slug', courseSlug)
      .single();
    
    if (error || !data) {
      console.warn('⚠️ Curso não encontrado, usando dados mock');
      return MOCK_COURSE;
    }
    
    return data as CourseLite;
  } catch (error) {
    console.warn('⚠️ Erro ao buscar curso, usando dados mock:', error);
    return MOCK_COURSE;
  }
}

export async function listModules(tenantId: string, courseId: string): Promise<ModuleLite[]> {
  try {
    const { data, error } = await supabase
      .from('modules')
      .select('id, slug, title, summary, cover, status, "order", updated_at')
      .eq('tenant_id', tenantId)
      .eq('course_id', courseId)
      .order('order', { ascending: true, nullsFirst: false })
      .order('id', { ascending: true }); // Fallback para id caso order seja null
    
    if (error) {
      console.warn('⚠️ Erro ao buscar módulos, usando dados mock:', error);
      return MOCK_MODULES;
    }
    
    return (data || []) as ModuleLite[];
  } catch (error) {
    console.warn('⚠️ Erro ao buscar módulos, usando dados mock:', error);
    return MOCK_MODULES;
  }
}

export async function getModuleBySlug(tenantId: string, courseId: string, moduleSlug: string): Promise<ModuleLite | null> {
  const { data, error } = await supabase
    .from('modules')
    .select('id, slug, title, summary, cover, status, updated_at')
    .eq('tenant_id', tenantId)
    .eq('course_id', courseId)
    .eq('slug', moduleSlug)
    .single();
  if (error) return null;
  return data as ModuleLite;
}

export async function listLessonsByModule(moduleId: string): Promise<LessonLite[]> {
  const { data, error } = await supabase
    .from('lessons')
    .select('id, module_id, title, description, video_url, provider, "order", duration, duration_seconds, is_free')
    .eq('module_id', moduleId)
    .order('order', { ascending: true });
  if (error) throw error;
  return (data || []) as LessonLite[];
}

export async function getCourseIdForModule(moduleId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('modules')
    .select('course_id')
    .eq('id', moduleId)
    .single();
  if (error) return null;
  return data?.course_id ?? null;
}

export async function getLessonById(lessonId: string): Promise<LessonLite | null> {
  console.log('🔍 getLessonById called with lessonId:', lessonId);
  
  const { data, error } = await supabase
    .from('lessons')
    .select('id, module_id, title, description, video_url, provider, "order", duration, duration_seconds, is_free')
    .eq('id', lessonId)
    .single();
  
  console.log('🔍 getLessonById result:', { data, error });
  
  if (error) {
    console.error('❌ getLessonById error:', error);
    return null;
  }
  
  return data as LessonLite;
}

export async function listCourses(tenantId: string): Promise<CourseLite[]> {
  const { data, error } = await supabase
    .from('courses')
    .select('id, slug, title, summary, cover, status, instructor, category, updated_at, created_at')
    .eq('tenant_id', tenantId)
    .order('id', { ascending: true });
  if (error) throw error;
  return (data || []) as CourseLite[];
}

// Debug function to list all lessons
export async function listAllLessons(): Promise<LessonLite[]> {
  console.log('🔍 listAllLessons called');
  
  const { data, error } = await supabase
    .from('lessons')
    .select('id, module_id, title, description, video_url, provider, "order", duration, duration_seconds, is_free')
    .order('id', { ascending: true });
  
  console.log('🔍 listAllLessons result:', { data, error });
  
  if (error) {
    console.error('❌ listAllLessons error:', error);
    throw error;
  }
  
  return (data || []) as LessonLite[];
}

// ==============================================
// NOVAS FUNÇÕES PARA SISTEMA DE MÍDIAS
// ==============================================

/**
 * Busca uma lição com todas as suas mídias associadas
 */
export async function getLessonWithMedia(lessonId: string): Promise<LessonWithMedia | null> {
  console.log('🔍 getLessonWithMedia called with lessonId:', lessonId);
  
  try {
    // Buscar dados da lição
    const lesson = await getLessonById(lessonId);
    if (!lesson) {
      console.warn('⚠️ Lição não encontrada:', lessonId);
      return null;
    }

    // Buscar mídias associadas
    const { data, error } = await supabase
      .from('lesson_media_mapping')
      .select(`
        id,
        lesson_id,
        media_mapping_id,
        slot,
        order_index,
        is_active,
        media_mapping:media_mapping_id (
          id,
          tenant_id,
          slot,
          library_url,
          media_type,
          title,
          provider,
          video_id,
          video_library_id,
          original_url,
          config,
          order_index,
          is_required,
          is_active,
          created_at,
          updated_at
        )
      `)
      .eq('lesson_id', lessonId)
      .eq('is_active', true)
      .order('order_index', { ascending: true });

    if (error) {
      console.error('❌ Erro ao buscar mídias da lição:', error);
      // Retornar lição sem mídias (fallback)
      return {
        ...lesson,
        mainVideo: undefined,
        supportingMedia: [],
        allMedia: []
      };
    }

    // Processar mídias
    const mediaMappings: MediaMapping[] = (data || [])
      .map(item => item.media_mapping)
      .filter(Boolean) as unknown as MediaMapping[];

    const mainVideo = mediaMappings.find(m => m.slot === 'main_video');
    const supportingMedia = mediaMappings.filter(m => m.slot.startsWith('supporting_media'));

    console.log('✅ Lição com mídias carregada:', {
      lessonId,
      mainVideo: mainVideo?.title,
      supportingMediaCount: supportingMedia.length
    });

    return {
      ...lesson,
      mainVideo,
      supportingMedia,
      allMedia: mediaMappings
    };

  } catch (error) {
    console.error('❌ Erro em getLessonWithMedia:', error);
    return null;
  }
}

/**
 * Busca o vídeo principal de uma lição
 */
export async function getMainVideoForLesson(lessonId: string): Promise<MediaMapping | null> {
  console.log('🔍 getMainVideoForLesson called with lessonId:', lessonId);
  
  try {
    const { data, error } = await supabase
      .from('lesson_media_mapping')
      .select(`
        media_mapping:media_mapping_id (
          id,
          tenant_id,
          slot,
          library_url,
          media_type,
          title,
          provider,
          video_id,
          video_library_id,
          original_url,
          config,
          order_index,
          is_required,
          is_active,
          created_at,
          updated_at
        )
      `)
      .eq('lesson_id', lessonId)
      .eq('slot', 'main_video')
      .eq('is_active', true)
      .order('order_index', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (error || !data?.media_mapping) {
      console.log('⚠️ Vídeo principal não encontrado para lição:', lessonId);
      return null;
    }

    console.log('✅ Vídeo principal encontrado:', (data.media_mapping as any).title);
    return data.media_mapping as unknown as MediaMapping;

  } catch (error) {
    console.error('❌ Erro em getMainVideoForLesson:', error);
    return null;
  }
}

/**
 * Busca mídias de apoio de uma lição
 */
export async function getSupportingMediaForLesson(lessonId: string): Promise<MediaMapping[]> {
  console.log('🔍 getSupportingMediaForLesson called with lessonId:', lessonId);
  
  try {
    const { data, error } = await supabase
      .from('lesson_media_mapping')
      .select(`
        media_mapping:media_mapping_id (
          id,
          tenant_id,
          slot,
          library_url,
          media_type,
          title,
          provider,
          video_id,
          video_library_id,
          original_url,
          config,
          order_index,
          is_required,
          is_active,
          created_at,
          updated_at
        )
      `)
      .eq('lesson_id', lessonId)
      .like('slot', 'supporting_media%')
      .eq('is_active', true)
      .order('order_index', { ascending: true });

    if (error) {
      console.error('❌ Erro ao buscar mídias de apoio:', error);
      return [];
    }

    const supportingMedia = (data || [])
      .map(item => item.media_mapping)
      .filter(Boolean) as unknown as MediaMapping[];

    console.log('✅ Mídias de apoio encontradas:', supportingMedia.length);
    return supportingMedia;

  } catch (error) {
    console.error('❌ Erro em getSupportingMediaForLesson:', error);
    return [];
  }
}

/**
 * Busca todas as mídias mapeadas para uma lição
 */
export async function getMediaMappingsByLesson(lessonId: string): Promise<MediaMapping[]> {
  console.log('🔍 getMediaMappingsByLesson called with lessonId:', lessonId);
  
  try {
    const { data, error } = await supabase
      .from('lesson_media_mapping')
      .select(`
        media_mapping:media_mapping_id (
          id,
          tenant_id,
          slot,
          library_url,
          media_type,
          title,
          provider,
          video_id,
          video_library_id,
          original_url,
          config,
          order_index,
          is_required,
          is_active,
          created_at,
          updated_at
        )
      `)
      .eq('lesson_id', lessonId)
      .eq('is_active', true)
      .order('order_index', { ascending: true });

    if (error) {
      console.error('❌ Erro ao buscar mídias da lição:', error);
      return [];
    }

    const mediaMappings = (data || [])
      .map(item => item.media_mapping)
      .filter(Boolean) as unknown as MediaMapping[];

    console.log('✅ Mídias da lição encontradas:', mediaMappings.length);
    return mediaMappings;

  } catch (error) {
    console.error('❌ Erro em getMediaMappingsByLesson:', error);
    return [];
  }
}

/**
 * Função auxiliar para obter URL do vídeo principal com fallback
 */
export function getMainVideoUrl(lesson: LessonLite, mediaMappings: MediaMapping[]): {
  url: string;
  provider: string;
  config: any;
} {
  const mainVideo = mediaMappings.find(m => m.slot === 'main_video' && m.is_active);
  
  if (mainVideo) {
    return {
      url: mainVideo.library_url,
      provider: mainVideo.provider,
      config: mainVideo.config
    };
  }
  
  // Fallback para sistema antigo
  return {
    url: lesson.video_url || '',
    provider: 'native',
    config: null
  };
}

// ==============================================
// FUNÇÕES PARA ANEXOS E LINKS EXTERNOS
// ==============================================

/**
 * Busca anexos (PDFs e documentos) de uma lição
 */
export async function getLessonAttachments(lessonId: string): Promise<LessonAttachment[]> {
  console.log('🔍 getLessonAttachments called with lessonId:', lessonId);
  
  try {
    // Buscar via lesson_media_mapping para encontrar associações da lição
    const { data, error } = await supabase
      .from('lesson_media_mapping')
      .select(`
        id,
        lesson_id,
        media_mapping_id,
        slot,
        order_index,
        is_active,
        media_mapping:media_mapping_id (
          id,
          tenant_id,
          slot,
          library_url,
          media_type,
          title,
          provider,
          config,
          order_index,
          is_required,
          is_active,
          video_id,
          video_library_id,
          original_url,
          created_at,
          updated_at
        )
      `)
      .eq('lesson_id', lessonId)
      .eq('slot', 'lesson_attachments')
      .eq('is_active', true)
      .order('order_index', { ascending: true });

    if (error) {
      console.error('❌ Erro ao buscar anexos da lição:', error);
      return [];
    }

    if (!data || data.length === 0) {
      console.log('ℹ️ Nenhuma associação de anexo encontrada para a lição');
      return [];
    }

    // Buscar dados do media_library para cada anexo
    const attachments = await Promise.all(
      data.map(async (item) => {
        const mediaMapping = item.media_mapping;
        if (!mediaMapping) return null;

        // Buscar dados do media_library usando o library_url como ID
        const { data: mediaLibrary, error: libraryError } = await supabase
          .from('media_library')
          .select('*')
          .eq('id', mediaMapping.library_url)
          .single();

        if (libraryError) {
          // Para URLs externas (Google Drive, etc), library_url já é o link direto
          // Retornar anexo sem media_library - LessonAttachments usa attachment.library_url como fallback
          console.warn('⚠️ media_library não encontrado, usando library_url direto:', libraryError.message);
          return {
            ...mediaMapping,
            media_library: undefined
          } as LessonAttachment;
        }

        return {
          ...mediaMapping,
          media_library: mediaLibrary as MediaLibraryItem
        } as LessonAttachment;
      })
    );

    const validAttachments = attachments.filter(Boolean) as LessonAttachment[];

    console.log('✅ Anexos encontrados:', validAttachments.length);
    return validAttachments;

  } catch (error) {
    console.error('❌ Erro em getLessonAttachments:', error);
    return [];
  }
}

/**
 * Busca links externos de uma lição
 */
export async function getLessonExternalLinks(lessonId: string): Promise<LessonExternalLink[]> {
  console.log('🔍 getLessonExternalLinks called with lessonId:', lessonId);
  
  try {
    // Buscar via lesson_media_mapping para encontrar associações da lição
    const { data, error } = await supabase
      .from('lesson_media_mapping')
      .select(`
        id,
        lesson_id,
        media_mapping_id,
        slot,
        order_index,
        is_active,
        media_mapping:media_mapping_id (
          id,
          tenant_id,
          slot,
          library_url,
          media_type,
          title,
          provider,
          config,
          order_index,
          is_required,
          is_active,
          video_id,
          video_library_id,
          original_url,
          created_at,
          updated_at
        )
      `)
      .eq('lesson_id', lessonId)
      .eq('slot', 'lesson_external_links')
      .eq('is_active', true)
      .order('order_index', { ascending: true });

    if (error) {
      console.error('❌ Erro ao buscar links externos da lição:', error);
      return [];
    }

    if (!data || data.length === 0) {
      console.log('ℹ️ Nenhuma associação de link externo encontrada para a lição');
      return [];
    }

    const links = data.map(item => {
      const mediaMapping = item.media_mapping;
      if (!mediaMapping) return null;

      return {
        ...mediaMapping,
        platform: mediaMapping.provider as 'youtube' | 'instagram' | 'website' | 'linkedin' | 'facebook' | 'twitter'
      } as LessonExternalLink;
    }).filter(Boolean) as LessonExternalLink[];

    console.log('✅ Links externos encontrados:', links.length);
    return links;

  } catch (error) {
    console.error('❌ Erro em getLessonExternalLinks:', error);
    return [];
  }
}

/**
 * Busca todos os recursos (anexos + links) de uma lição
 */
export async function getLessonResources(lessonId: string): Promise<{
  attachments: LessonAttachment[];
  externalLinks: LessonExternalLink[];
}> {
  console.log('🔍 getLessonResources called with lessonId:', lessonId);
  
  try {
    const [attachments, externalLinks, materials] = await Promise.all([
      getLessonAttachments(lessonId),
      getLessonExternalLinks(lessonId),
      listLessonMaterials(lessonId).catch(() => [])
    ]);

    const canonicalAttachments = materials.map(material => ({
      id: material.id,
      tenant_id: material.tenant_id,
      slot: 'lesson_attachments',
      library_url: material.resolved_url || '',
      media_type: material.material_type === 'LINK' ? 'link' : 'document',
      title: material.title,
      provider: 'native',
      config: { description: material.description, materialType: material.material_type, source: material.source },
      order_index: material.sort_order,
      is_required: false,
      is_active: true,
      created_at: material.created_at,
      updated_at: material.created_at,
      media_library: material.material_type === 'LINK' ? undefined : {
        id: material.id, tenant_id: material.tenant_id, title: material.title,
        filename: material.original_filename || material.title, media_type: 'document',
        url: material.resolved_url || '', provider: 'supabase', config: material.provenance,
        size_bytes: material.size_bytes || undefined, mime_type: material.mime_type || undefined,
        tags: [material.material_type, material.source], is_public: false, usage_count: 0,
        created_at: material.created_at, updated_at: material.created_at,
      }
    })) as LessonAttachment[];

    console.log('✅ Recursos da lição carregados:', {
      attachments: attachments.length + canonicalAttachments.length,
      externalLinks: externalLinks.length
    });

    return {
      attachments: [...canonicalAttachments, ...attachments],
      externalLinks
    };

  } catch (error) {
    console.error('❌ Erro em getLessonResources:', error);
    return {
      attachments: [],
      externalLinks: []
    };
  }
}
